// src/App.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import "./styles/global.css";

import { AuthProvider } from "./context/AuthContext";
import { useAuth }      from "./context/AuthContext";
import { useToast }     from "./hooks/useToast";
import { API }          from "./utils/constants";

import Navbar             from "./components/Navbar";
import Hero               from "./components/Hero";
import NewsFeed           from "./components/NewsFeed";
import Sidebar            from "./components/Sidebar";
import FeaturesSection    from "./components/FeaturesSection";
import MinecraftSection   from "./components/MinecraftSection";
import DiscordSection     from "./components/DiscordSection";
import Footer             from "./components/Footer";
import Toast              from "./components/Toast";
import AuthModal          from "./components/AuthModal";
import NewsPostModal      from "./components/NewsPostModal";
import AdminNewsPage      from "./components/AdminNewsPage";
import StoreModal         from "./components/StoreModal";
import MinecraftStorePage from "./components/MinecraftStorePage";
import MinecraftPage      from "./components/MinecraftPage";
import SettingsModal      from "./components/SettingsModal";
import ForumPage          from "./components/ForumPage";
import ClothingStorePage  from "./components/ClothingStorePage";

function parsePath() {
  const path = window.location.pathname.replace(/\/$/, "") || "/";

  if (path === "/store") {
    return {
      page: "home",
      tab: null,
      modal: "store",
      newsSlug: null,
    };
  }

  if (path === "/store/ranks") {
    return {
      page: "mcstore",
      tab: "ranks",
      modal: null,
      newsSlug: null,
    };
  }

  if (path === "/store/coins") {
    return {
      page: "mcstore",
      tab: "coins",
      modal: null,
      newsSlug: null,
    };
  }

  if (path === "/minecraft") {
    return {
      page: "minecraft",
      tab: null,
      modal: null,
      newsSlug: null,
    };
  }

  if (path === "/forum") {
    return {
      page: "forum",
      tab: null,
      modal: null,
      newsSlug: null,
    };
  }

  if (path === "/merch") {
    return {
      page: "merch",
      tab: null,
      modal: null,
      newsSlug: null,
    };
  }

  const mcTabMatch = path.match(/^\/minecraft\/(.+)$/);

  if (mcTabMatch) {
    const t =
      mcTabMatch[1] === "leaderboard"
        ? "stats"
        : mcTabMatch[1];

    return {
      page: "minecraft",
      tab: t,
      modal: null,
      newsSlug: null,
    };
  }

  const newsMatch = path.match(/^\/news\/(.+)$/);

  if (newsMatch) {
    return {
      page: "home",
      tab: null,
      modal: null,
      newsSlug: newsMatch[1],
    };
  }

  return {
    page: "home",
    tab: null,
    modal: null,
    newsSlug: null,
  };
}

function nav(path) {
  window.history.pushState(null, "", path);
}

function AppInner() {
  const {
    loading: authLoading,
    authUser,
    accessToken,
    logout,
  } = useAuth();

  const {
    toast,
    showToast,
  } = useToast();

  const initial = parsePath();

  const [page, setPage] = useState(
    initial.page
  );

  const [mcStoreTab, setMcStoreTab] =
    useState(
      initial.tab || "ranks"
    );

  const MINECRAFT_TABS = new Set([
    "about",
    "rules",
    "map",
    "factions",
    "stats",
  ]);

  const [mcPageTab, setMcPageTab] =
    useState(
      initial.page === "minecraft" &&
      initial.tab &&
      MINECRAFT_TABS.has(initial.tab)
        ? initial.tab
        : "about"
    );

  const [storeModal, setStoreModal] =
    useState(
      initial.modal === "store"
    );

  const [authModal, setAuthModal] =
    useState(null);

  const [adminOpen, setAdminOpen] =
    useState(false);

  const [
    settingsOpen,
    setSettingsOpen,
  ] = useState(false);

  const [
    merchEmail,
    setMerchEmail,
  ] = useState("");

  const [
    newsPosts,
    setNewsPosts,
  ] = useState([]);

  const [
    newsLoading,
    setNewsLoading,
  ] = useState(true);

  const [cart, setCart] =
    useState([]);

  const [
    activeNewsSlug,
    setActiveNewsSlug,
  ] = useState(
    initial.newsSlug
  );

  const [
    forumTarget,
    setForumTarget,
  ] = useState({
    cat: null,
    subcat: null,
  });

  const navRef = useRef(null);

  const [
    heroHeight,
    setHeroHeight,
  ] = useState("100vh");

  useEffect(() => {
    const onPop = () => {
      const {
        page: p,
        tab: t,
        modal: m,
        newsSlug: s,
      } = parsePath();

      setPage(p);

      if (
        p === "mcstore" &&
        t
      ) {
        setMcStoreTab(t);
      }

      if (
        p === "minecraft" &&
        t
      ) {
        setMcPageTab(t);
      }

      setStoreModal(
        m === "store"
      );

      setActiveNewsSlug(s);
    };

    window.addEventListener(
      "popstate",
      onPop
    );

    return () =>
      window.removeEventListener(
        "popstate",
        onPop
      );
  }, []);

  const fetchNews = () => {
    fetch(
      `${API}/news/home`
    )
      .then((r) => r.json())
      .then((d) =>
        setNewsPosts(
          d.posts || []
        )
      )
      .catch(() =>
        setNewsPosts([])
      )
      .finally(() =>
        setNewsLoading(false)
      );
  };

  useEffect(
    fetchNews,
    []
  );

  useEffect(() => {
    const calc = () => {
      const nH =
        navRef.current
          ?.offsetHeight || 62;

      setHeroHeight(
        `calc(100vh - ${nH}px)`
      );
    };

    calc();

    window.addEventListener(
      "resize",
      calc
    );

    return () =>
      window.removeEventListener(
        "resize",
        calc
      );
  }, []);

  useEffect(() => {
    window.scrollTo(
      0,
      0
    );
  }, [page]);

  const openStorePicker =
    useCallback(() => {
      nav("/store");
      setStoreModal(true);
    }, []);

  const openMcStore =
    useCallback(
      (
        tab = "ranks"
      ) => {
        nav(
          `/store/${tab}`
        );

        setStoreModal(false);
        setMcStoreTab(tab);
        setPage("mcstore");
      },
      []
    );

  const goHome =
    useCallback(() => {
      nav("/");
      setPage("home");
      setStoreModal(false);
    }, []);

  const closeStoreModal =
    useCallback(() => {
      nav("/");
      setStoreModal(false);
    }, []);

  const openNewsPost =
    useCallback(
      (slug) => {
        nav(
          `/news/${slug}`
        );

        setActiveNewsSlug(
          slug
        );
      },
      []
    );

  const closeNewsPost =
    useCallback(() => {
      nav("/");
      setActiveNewsSlug(null);
    }, []);

  const openForum =
    useCallback(
      (
        cat = null,
        subcat = null
      ) => {
        nav("/forum");

        setForumTarget({
          cat,
          subcat,
        });

        setPage("forum");
      },
      []
    );

  const goMerch =
    useCallback(() => {
      nav("/merch");
      setPage("merch");
    }, []);

  if (authLoading) {
    return null;
  }

  const copyIP = () => {
    navigator.clipboard
      .writeText(
        "play.theonesabove.org"
      )
      .then(() =>
        showToast(
          "IP Copied: play.theonesabove.org"
        )
      )
      .catch(() =>
        showToast(
          "Server: play.theonesabove.org"
        )
      );
  };

  return (
    <>
      {page !== "merch" && (
        <Navbar
          navRef={navRef}
          onOpenModal={
            setAuthModal
          }
          onOpenStore={
            openStorePicker
          }
          onOpenAdmin={() =>
            setAdminOpen(true)
          }
          onOpenSettings={() =>
            setSettingsOpen(
              true
            )
          }
          onOpenMerch={
            goMerch
          }
          cartCount={cart.reduce(
            (s, i) =>
              s + i.qty,
            0
          )}
          currentPage={page}
          onGoHome={goHome}
        />
      )}

      {page === "home" && (
        <>
          <Hero
            heroHeight={
              heroHeight
            }
            onOpenStore={
              openStorePicker
            }
            onCopyIP={
              copyIP
            }
          />

          <div className="toa-main-outer">
            <div className="toa-main">
              <NewsFeed
                posts={
                  newsPosts
                }
                loading={
                  newsLoading
                }
                onOpenAdminPost={() =>
                  setAdminOpen(
                    true
                  )
                }
                onOpenPost={
                  openNewsPost
                }
              />

              <Sidebar
                showToast={
                  showToast
                }
                merchEmail={
                  merchEmail
                }
                setMerchEmail={
                  setMerchEmail
                }
                onOpenMcStore={
                  openMcStore
                }
                onOpenForum={
                  openForum
                }
              />
            </div>
          </div>

          <FeaturesSection />

          <MinecraftSection
            onCopyIP={
              copyIP
            }
            onOpenStore={
              openStorePicker
            }
            onOpenMcStore={
              openMcStore
            }
            onOpenMcPage={() => {
              nav(
                "/minecraft"
              );

              setPage(
                "minecraft"
              );
            }}
          />

          <DiscordSection />

          <Footer />
        </>
      )}

      {page === "mcstore" && (
        <MinecraftStorePage
          initialTab={
            mcStoreTab
          }
          onClose={
            goHome
          }
          onOpenAuth={
            setAuthModal
          }
        />
      )}

      {page === "forum" && (
        <ForumPage
          showToast={
            showToast
          }
          onGoHome={
            goHome
          }
          initialCat={
            forumTarget.cat
          }
          initialSubcat={
            forumTarget.subcat
          }
        />
      )}

      {page === "minecraft" && (
        <MinecraftPage
          initialTab={
            mcPageTab
          }
          onTabChange={(
            id
          ) => {
            setMcPageTab(
              id
            );

            window.history.replaceState(
              null,
              "",
              id === "about"
                ? "/minecraft"
                : "/minecraft/" +
                    id
            );
          }}
          onClose={
            goHome
          }
          onOpenMcStore={
            openMcStore
          }
          onCopyIP={
            copyIP
          }
          showToast={
            showToast
          }
          onOpenForum={
            openForum
          }
        />
      )}

      {page === "merch" && (
        <ClothingStorePage
          onGoHome={goHome}
          authUser={authUser}
          accessToken={accessToken}
          onOpenAuth={
            setAuthModal
          }
          onOpenSettings={() =>
            setSettingsOpen(
              true
            )
          }
          onLogout={logout}
        />
      )}

      <Toast toast={toast} />

      {storeModal && (
        <StoreModal
          onClose={
            closeStoreModal
          }
          showToast={
            showToast
          }
          onOpenMcStore={
            openMcStore
          }
        />
      )}

      {authModal && (
        <AuthModal
          modal={
            authModal
          }
          onClose={() =>
            setAuthModal(
              null
            )
          }
          showToast={
            showToast
          }
        />
      )}

      {settingsOpen && (
        <SettingsModal
          onClose={() =>
            setSettingsOpen(
              false
            )
          }
          showToast={
            showToast
          }
        />
      )}

      {activeNewsSlug && (
        <NewsPostModal
          post={
            newsPosts.find(
              (p) =>
                p.slug ===
                activeNewsSlug
            ) || {
              slug:
                activeNewsSlug,
            }
          }
          onClose={
            closeNewsPost
          }
        />
      )}

      {adminOpen &&
        authUser?.role ===
          "admin" && (
          <AdminNewsPage
            onClose={() => {
              setAdminOpen(
                false
              );

              fetchNews();
            }}
            showToast={
              showToast
            }
          />
        )}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}